import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Select, DatePicker, Spin, Alert } from 'antd';
import {
    TeamOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    ManOutlined,
    WomanOutlined,
    BankOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { StatCard } from '../../../design-system';
import demographicsService from '../../../service/ApiServiceDemographics';
import GenderPieChart from './GenderPieChart';
import AgeDistributionChart from './AgeDistributionChart';
import TenureDistributionChart from './TenureDistributionChart';
import DepartmentBreakdownChart from './DepartmentBreakdownChart';
import OfficeBreakdownChart from './OfficeBreakdownChart';
import DemographicsDrilldownModal from './DemographicsDrilldownModal';
import { generateCSV, downloadCSV } from '../../../utils/currencyUtils';
import logger from '../../../utils/logger';



const QUICK_SELECT_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: '1m', label: '1 month ago' },
    { value: '3m', label: '3 months ago' },
    { value: '6m', label: '6 months ago' },
    { value: '1y', label: '1 year ago' },
    { value: 'custom', label: 'Custom date' },
];

function getDateFromPreset(preset) {
    const now = dayjs();
    switch (preset) {
        case '1m': return now.subtract(1, 'month');
        case '3m': return now.subtract(3, 'month');
        case '6m': return now.subtract(6, 'month');
        case '1y': return now.subtract(1, 'year');
        default: return now;
    }
}

const DemographicsDashboard = () => {
    const [quickSelect, setQuickSelect] = useState('today');
    const [snapshotDate, setSnapshotDate] = useState(dayjs());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Drill-down modal state
    const [drilldown, setDrilldown] = useState({
        visible: false,
        title: '',
        subtitle: '',
        dimension: '',
        value: undefined,
    });

    const fetchData = useCallback(async (date) => {
        setLoading(true);
        setError(null);
        try {
            const dateStr = date.format('YYYY-MM-DD');
            const result = await demographicsService.getSnapshot(dateStr);
            setData(result);
        } catch (err) {
            logger.error('[Demographics] fetch error:', err);
            setError(err.message || 'Failed to load demographics data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(snapshotDate);
    }, [snapshotDate, fetchData]);

    const handleQuickSelect = (value) => {
        setQuickSelect(value);
        if (value !== 'custom') {
            const newDate = getDateFromPreset(value);
            setSnapshotDate(newDate);
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            setSnapshotDate(date);
            setQuickSelect('custom');
        }
    };

    // Drill-down handlers
    const openDrilldown = (dimension, value, title) => {
        setDrilldown({ visible: true, title, subtitle: '', dimension, value });
    };

    const closeDrilldown = () => {
        setDrilldown(prev => ({ ...prev, visible: false }));
    };

    // Chart CSV export handler (exports aggregate chart data, no API call)
    const handleChartExport = (chartType, chartData) => {
        let headers, rows, filename;
        const dateStr = snapshotDate.format('YYYY-MM-DD');

        switch (chartType) {
            case 'gender':
                headers = ['Gender', 'Count'];
                rows = chartData.map(d => [d.gender, d.count]);
                filename = `demographics-gender-${dateStr}.csv`;
                break;
            case 'age':
                headers = ['Age Group', 'Total', 'Male', 'Female'];
                rows = chartData.map(d => [d.ageGroup, d.count, d.male, d.female]);
                filename = `demographics-age-${dateStr}.csv`;
                break;
            case 'tenure':
                headers = ['Tenure Group', 'Count'];
                rows = chartData.map(d => [d.tenureGroup, d.count]);
                filename = `demographics-tenure-${dateStr}.csv`;
                break;
            case 'department':
                headers = ['Department', 'Total', 'Male', 'Female'];
                rows = chartData.map(d => [d.department, d.count, d.male, d.female]);
                filename = `demographics-department-${dateStr}.csv`;
                break;
            case 'office':
                headers = ['Office', 'Country', 'Total', 'Male', 'Female'];
                rows = chartData.map(d => [d.office, d.country, d.count, d.male, d.female]);
                filename = `demographics-office-${dateStr}.csv`;
                break;
            default:
                return;
        }

        const csv = generateCSV(headers, rows);
        downloadCSV(csv, filename);
    };

    const summary = data?.summary || {};
    const dateStr = snapshotDate.format('YYYY-MM-DD');

    return (
        <div>
            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontWeight: 500 }}>Snapshot date:</span>
                <Select
                    value={quickSelect}
                    onChange={handleQuickSelect}
                    options={QUICK_SELECT_OPTIONS}
                    style={{ width: 160 }}
                />
                <DatePicker
                    value={snapshotDate}
                    onChange={handleDateChange}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    style={{ height: 38 }}
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
            </div>

            {error && (
                <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    style={{ marginBottom: 20 }}
                />
            )}

            <Spin spinning={loading}>
                {/* Row 1: Stat Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={8} lg={6} xxl={4}>
                        <StatCard
                            icon={<TeamOutlined style={{ color: '#FED230', fontSize: 20 }} />}
                            value={summary.totalEmployees ?? '-'}
                            label="Total Employees"
                            variant="bordered"
                            onClick={() => openDrilldown('all', null, 'All Employees')}
                        />
                    </Col>
                    <Col xs={12} sm={8} lg={6} xxl={4}>
                        <StatCard
                            icon={<CalendarOutlined style={{ color: '#FED230', fontSize: 20 }} />}
                            value={summary.avgAge ?? '-'}
                            label="Average Age"
                            variant="bordered"
                            onClick={() => openDrilldown('all', null, 'All Employees')}
                        />
                    </Col>
                    <Col xs={12} sm={8} lg={6} xxl={4}>
                        <StatCard
                            icon={<ClockCircleOutlined style={{ color: '#FED230', fontSize: 20 }} />}
                            value={summary.avgTenureYears ?? '-'}
                            label="Avg Tenure"
                            suffix=" yrs"
                            variant="bordered"
                            onClick={() => openDrilldown('all', null, 'All Employees')}
                        />
                    </Col>
                    <Col xs={12} sm={8} lg={6} xxl={4}>
                        <StatCard
                            icon={<ManOutlined style={{ color: '#1B1B1B', fontSize: 20 }} />}
                            value={summary.maleCount ?? '-'}
                            label="Male"
                            suffix={summary.totalEmployees > 0 && summary.maleCount != null ? ` (${Math.round(summary.maleCount / summary.totalEmployees * 100)}%)` : ''}
                            variant="bordered"
                            onClick={() => openDrilldown('gender', 'Male', 'Male Employees')}
                        />
                    </Col>
                    <Col xs={12} sm={8} lg={6} xxl={4}>
                        <StatCard
                            icon={<WomanOutlined style={{ color: '#FED230', fontSize: 20 }} />}
                            value={summary.femaleCount ?? '-'}
                            label="Female"
                            suffix={summary.totalEmployees > 0 && summary.femaleCount != null ? ` (${Math.round(summary.femaleCount / summary.totalEmployees * 100)}%)` : ''}
                            variant="bordered"
                            onClick={() => openDrilldown('gender', 'Female', 'Female Employees')}
                        />
                    </Col>
                    <Col xs={12} sm={8} lg={6} xxl={4}>
                        <StatCard
                            icon={<BankOutlined style={{ color: '#FED230', fontSize: 20 }} />}
                            value={summary.departmentCount ?? '-'}
                            label="Departments"
                            variant="bordered"
                            onClick={() => openDrilldown('all', null, 'All Employees')}
                        />
                    </Col>
                </Row>

                {/* Row 2: Gender Donut + Age Distribution */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} lg={8}>
                        <GenderPieChart
                            data={data?.gender}
                            onSegmentClick={(item) => openDrilldown('gender', item.gender, `${item.gender} Employees`)}
                            onExportCSV={handleChartExport}
                        />
                    </Col>
                    <Col xs={24} lg={16}>
                        <AgeDistributionChart
                            data={data?.age}
                            onSegmentClick={(item) => openDrilldown('age', item.ageGroup, `Age: ${item.ageGroup}`)}
                            onExportCSV={handleChartExport}
                        />
                    </Col>
                </Row>

                {/* Row 3: Tenure + Department */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} lg={12}>
                        <TenureDistributionChart
                            data={data?.tenure}
                            onSegmentClick={(item) => openDrilldown('tenure', item.tenureGroup, `Tenure: ${item.tenureGroup}`)}
                            onExportCSV={handleChartExport}
                        />
                    </Col>
                    <Col xs={24} lg={12}>
                        <DepartmentBreakdownChart
                            data={data?.departments}
                            onSegmentClick={(item) => openDrilldown('department', item.departmentId !== null ? item.departmentId : 'null', item.department)}
                            onExportCSV={handleChartExport}
                        />
                    </Col>
                </Row>

                {/* Row 4: Office Breakdown */}
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <OfficeBreakdownChart
                            data={data?.offices}
                            onSegmentClick={(item) => openDrilldown('office', item.officeId, `${item.office}${item.country ? ' (' + item.country + ')' : ''}`)}
                            onExportCSV={handleChartExport}
                        />
                    </Col>
                </Row>
            </Spin>

            {/* Drill-down Modal */}
            <DemographicsDrilldownModal
                visible={drilldown.visible}
                onClose={closeDrilldown}
                title={drilldown.title}
                subtitle={drilldown.subtitle}
                snapshotDate={dateStr}
                dimension={drilldown.dimension}
                value={drilldown.value}
            />
        </div>
    );
};

export default DemographicsDashboard;
